import { useEffect, useState } from 'react';
import { Button, Card } from '@plick/ui';
import { IconExternalLink, IconX } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { plickTheme } from '@theme/index';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from '../CommunicationsModal/hooks/useRespondAnnouncement';

interface Props {
  announcement: Announcement;
  /** Usuario clickeó la ✕ → descarte permanente (localStorage). */
  onDismiss: () => void;
  /** Usuario respondió una opción → ocultar solo en la sesión actual. */
  onRespond: () => void;
}

export const CommunicationsCard = ({ announcement, onDismiss, onRespond }: Props) => {
  // Instancia local del hook → isSubmitting solo afecta a esta tarjeta.
  const { respond, isSubmitting, submittingOptionId } = useRespondAnnouncement();

  const bgColor = announcement.bg_color || plickTheme.colors.light;
  const textColor = announcement.text_color || plickTheme.colors.dark;

  const initialDelay = announcement.delay_before_close ?? 0;
  const [canClose, setCanClose] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const t = setTimeout(() => setCanClose(true), initialDelay * 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialDelay]);

  // Persistente: la X solo aparece si ya respondió (mismo contrato que el banner).
  const isPersistent = announcement.is_persistent === true;
  const hasResponded = Boolean(announcement.user_response);
  const showCloseButton = (!isPersistent || hasResponded) && canClose;

  const hasMainUrl = announcement.url && !announcement.options?.length;

  return (
    <Card padding="0rem" backgroundColor={bgColor} borderRadius="0.75rem">
      <div style={{ color: textColor }} className="relative p-4 sm:p-5 flex flex-col gap-3 overflow-hidden rounded-xl">
        {showCloseButton && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Descartar tarjeta"
            className="absolute top-2 right-2 p-1 rounded hover:opacity-80 transition-opacity"
            style={{ color: textColor }}
          >
            <IconX size={16} />
          </button>
        )}

        <div className="pr-6 min-w-0">
          <h3 className="font-bold text-base sm:text-lg break-words">{announcement.title}</h3>
          {announcement.subtitle && <p className="text-sm opacity-90 mt-0.5 break-words">{announcement.subtitle}</p>}
        </div>

        {(announcement.text || announcement.long_description) && (
          <p className="text-sm opacity-80 whitespace-pre-wrap break-words">
            {announcement.text || announcement.long_description}
          </p>
        )}

        {announcement.options && announcement.options.length > 0 && (
          <AnnouncementOptionButtons
            options={announcement.options}
            announcementId={announcement.uuid}
            userResponse={announcement.user_response}
            bgColor={bgColor}
            textColor={textColor}
            isSubmitting={isSubmitting}
            submittingOptionId={submittingOptionId}
            isPersistent={announcement.is_persistent === true}
            variant="card"
            respond={respond}
            onComplete={onRespond}
          />
        )}

        {hasMainUrl && (
          <div className="self-start [&>button]:h-9 [&>button]:px-4 [&>button]:text-sm">
            <Button.Primary
              text="Más información"
              leftIcon={<IconExternalLink size={14} />}
              onClick={() => {
                // onRespond despues del open por la misma razon que en Banner:
                // un card is_persistent con url top-level y options=[] no
                // tendria como cerrarse (la X esta oculta hasta hasResponded).
                window.open(announcement.url ?? '#', '_blank', 'noopener,noreferrer');
                onRespond();
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};
